<template>
  <div class="link-suggestions">
    <p class="intro">{{ $t("message.infoauto_link.intro") }}</p>

    <div class="toolbar">
      <input
        type="text"
        v-model="searchInput"
        class="border border-gray rounded-md p-2 search"
        :placeholder="$t('message.infoauto_link.search')"
      />
      <select v-model="filters.scope" class="regular bordered scope">
        <option value="suggested">
          {{ $t("message.infoauto_link.scope_suggested") }}
        </option>
        <option value="duplicates">
          {{ $t("message.infoauto_link.scope_duplicates") }}
        </option>
        <option value="all">{{ $t("message.infoauto_link.scope_all") }}</option>
      </select>
    </div>

    <div class="index-info" v-if="summary">
      <span>
        {{
          $t("message.infoauto_link.summary", {
            total: summary.total,
            unlinked: summary.unlinked
          })
        }}
      </span>
      <button
        v-if="summary.possibleDuplicates > 0 && filters.scope !== 'duplicates'"
        class="link duplicates-link"
        @click="filters.scope = 'duplicates'"
      >
        <i class="mdi mdi-content-duplicate"></i>
        {{
          $t("message.infoauto_link.duplicates_count", {
            count: summary.possibleDuplicates
          })
        }}
      </button>
      <span>
        {{
          $t("message.infoauto_link.index_info", {
            count: summary.infoautoCustomers,
            date: summary.infoautoFetchedAt
          })
        }}
      </span>
      <button class="link" :disabled="loading" @click="load(true)">
        <i class="mdi mdi-refresh"></i>
        {{ $t("message.infoauto_link.refresh_index") }}
      </button>
    </div>

    <p v-if="filters.scope === 'duplicates'" class="duplicates-hint">
      {{ $t("message.infoauto_link.duplicates_hint") }}
    </p>

    <div v-if="loading" class="state">
      <BaseSmallSpinner />
      {{ $t("message.infoauto_link.loading_index") }}
    </div>
    <div v-else-if="customers.length === 0" class="state">
      {{ $t("message.infoauto_link.empty") }}
    </div>

    <div v-else class="rows">
      <div
        v-for="customer in customers"
        :key="customer.id"
        class="row"
        :class="{ 'is-duplicate': customer.candidates.length > 1 }"
      >
        <div v-if="customer.candidates.length > 1" class="duplicate-badge">
          <i class="mdi mdi-content-duplicate"></i>
          {{
            $t("message.infoauto_link.duplicate_badge", {
              count: customer.candidates.length
            })
          }}
        </div>
        <!-- Web user -->
        <div class="web-user">
          <button
            class="user-name"
            @click="$emit('openWebCustomer', customer.id)"
          >
            {{ customer.name }} {{ customer.surname }}
          </button>
          <div class="muted">{{ customer.email }}</div>
          <div class="muted" v-if="customer.phone">{{ customer.phone }}</div>
          <div class="muted" v-if="customer.companyName">
            {{ customer.companyName }}
          </div>
          <div class="activity">
            <template v-if="Number(customer.ordersTotal) > 0">
              {{
                $t("message.infoauto_link.orders", {
                  total: customer.ordersTotal,
                  billed: customer.ordersBilled
                })
              }}
              ·
              {{
                $t("message.infoauto_link.last_order", {
                  date: formatDate(customer.lastOrder)
                })
              }}
            </template>
            <template v-else>{{
              $t("message.infoauto_link.no_orders")
            }}</template>
          </div>
        </div>

        <!-- Infoauto candidates -->
        <div class="candidates">
          <div v-if="customer.candidates.length === 0" class="state small">
            {{ $t("message.infoauto_link.no_candidates") }}
          </div>
          <div
            v-for="candidate in customer.candidates"
            :key="candidate.infoautoId"
            class="candidate"
          >
            <div class="candidate-info">
              <div class="candidate-name">
                {{ candidate.name || candidate.infoautoId }}
                <span class="muted">{{ candidate.infoautoId }}</span>
              </div>
              <div class="muted" v-if="candidate.email">
                {{ candidate.email }}
              </div>
              <div class="chips">
                <span
                  v-for="reason in candidate.reasons"
                  :key="reason"
                  class="chip"
                  :class="`chip-${reason}`"
                >
                  {{ reasonLabel(reason, candidate) }}
                </span>
              </div>
            </div>
            <div class="candidate-actions">
              <button
                class="btn-success-button"
                :disabled="!!processing[customer.id]"
                @click="accept(customer, candidate)"
              >
                {{ $t("message.infoauto_link.connect") }}
              </button>
              <button
                class="btn-dark-button"
                :disabled="!!processing[customer.id]"
                @click="reject(customer, candidate)"
              >
                {{ $t("message.infoauto_link.reject") }}
              </button>
              <button
                class="link"
                @click="$emit('openInfoauto', candidate.infoautoId)"
              >
                {{ $t("message.infoauto_link.view") }}
              </button>
            </div>
          </div>

          <!-- Phone is searched on demand: the infoauto list has no phones -->
          <div class="phone-search" v-if="customer.phone">
            <span v-if="phoneSearch[customer.id] === 'loading'" class="muted">
              <BaseSmallSpinner />
              {{ $t("message.infoauto_link.searching_phone") }}
            </span>
            <span v-else-if="phoneSearch[customer.id] === 'none'" class="muted">
              {{ $t("message.infoauto_link.no_phone_match") }}
            </span>
            <span v-else-if="phoneSearch[customer.id] > 0" class="muted">
              {{
                $t("message.infoauto_link.phone_shared", {
                  count: phoneSearch[customer.id]
                })
              }}
            </span>
            <button
              v-else-if="!phoneSearch[customer.id]"
              class="link"
              @click="searchPhone(customer)"
            >
              <i class="mdi mdi-phone-outline"></i>
              {{ $t("message.infoauto_link.search_phone") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination" v-if="totalPages > 1">
      <button
        class="btn-dark-button"
        :disabled="filters.page <= 1 || loading"
        @click="filters.page--"
      >
        {{ $t("message.infoauto_link.previous") }}
      </button>
      <span>
        {{
          $t("message.infoauto_link.page", {
            page: filters.page,
            pages: totalPages
          })
        }}
      </span>
      <button
        class="btn-dark-button"
        :disabled="filters.page >= totalPages || loading"
        @click="filters.page++"
      >
        {{ $t("message.infoauto_link.next") }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  getInfoautoLinkSuggestions,
  getInfoautoPhoneCandidates,
  decideInfoautoLink
} from "@/api/store/CustomersService.js";

export default {
  name: "InfoautoLinkSuggestions",
  data() {
    return {
      searchInput: "",
      filters: {
        page: 1,
        limit: 20,
        search: "",
        scope: "suggested"
      },
      customers: [],
      summary: null,
      loading: false,
      // customerId -> true while a decision is being saved
      processing: {},
      // customerId -> 'loading' | 'done' | 'none' | <phone shared by N>
      phoneSearch: {},
      loadToken: 0
    };
  },
  computed: {
    totalPages() {
      const total = this.summary ? this.summary.total : 0;
      return Math.max(1, Math.ceil(total / this.filters.limit));
    }
  },
  watch: {
    searchInput(val) {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.filters.search = val;
      }, 300);
    },
    "filters.scope"() {
      this.filters.page = 1;
    },
    filters: {
      deep: true,
      handler() {
        this.load();
      }
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this._searchTimer);
  },
  methods: {
    async load(refresh = false) {
      const token = ++this.loadToken;
      this.loading = true;
      try {
        const res = await getInfoautoLinkSuggestions({
          ...this.filters,
          refresh
        });
        if (token !== this.loadToken) return;
        this.customers = res.customers;
        this.summary = res;
        this.processing = {};
        this.phoneSearch = {};
      } catch (e) {
        if (token !== this.loadToken) return;
        this.customers = [];
        this.$toast.open({
          message: e.response?.data?.message || "Error",
          type: "error",
          position: "top-right"
        });
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
    async searchPhone(customer) {
      this.$set(this.phoneSearch, customer.id, "loading");
      try {
        const res = await getInfoautoPhoneCandidates(customer.id);
        const sharedBy = Number(res.phoneSharedBy) || 0;
        if (sharedBy > 0) {
          this.$set(this.phoneSearch, customer.id, sharedBy);
          return;
        }
        res.candidates.forEach(found => {
          const existing = customer.candidates.find(
            c => c.infoautoId === found.infoautoId
          );
          if (existing) {
            if (!existing.reasons.includes("phone")) {
              existing.reasons.push("phone");
            }
          } else {
            customer.candidates.push(found);
          }
        });
        this.$set(
          this.phoneSearch,
          customer.id,
          res.candidates.length > 0 ? "done" : "none"
        );
      } catch (e) {
        this.$delete(this.phoneSearch, customer.id);
        this.$toast.open({
          message: e.response?.data?.message || "Error",
          type: "error",
          position: "top-right"
        });
      }
    },
    reasonLabel(reason, candidate) {
      if (reason === "orders") {
        return this.$t("message.infoauto_link.reason_orders", {
          count: candidate.ordersBilled
        });
      }
      return this.$t(`message.infoauto_link.reason_${reason}`);
    },
    userName(customer) {
      return `${customer.name} ${customer.surname}`.trim() || customer.email;
    },
    async accept(customer, candidate) {
      const account = candidate.name || candidate.infoautoId;
      if (
        !confirm(
          this.$t("message.infoauto_link.confirm_connect", {
            user: this.userName(customer),
            account
          })
        )
      ) {
        return;
      }
      await this.decide(customer, candidate, "accepted", () => {
        // Connected users leave the list
        this.customers = this.customers.filter(c => c.id !== customer.id);
        this.summary.total = Math.max(0, this.summary.total - 1);
        this.summary.unlinked = Math.max(0, this.summary.unlinked - 1);
        this.$toast.open({
          message: this.$t("message.infoauto_link.connected", {
            user: this.userName(customer),
            account
          }),
          type: "success",
          position: "top-right"
        });
      });
    },
    async reject(customer, candidate) {
      await this.decide(customer, candidate, "rejected", () => {
        customer.candidates = customer.candidates.filter(
          c => c.infoautoId !== candidate.infoautoId
        );
        this.$toast.open({
          message: this.$t("message.infoauto_link.rejected"),
          type: "info",
          position: "top-right"
        });
      });
    },
    async decide(customer, candidate, decision, onSuccess) {
      this.$set(this.processing, customer.id, true);
      try {
        await decideInfoautoLink(
          customer.id,
          candidate.infoautoId,
          decision,
          candidate.reasons
        );
        onSuccess();
      } catch (e) {
        this.$toast.open({
          message:
            e.response?.data?.message || this.$t("message.infoauto_link.error"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.$delete(this.processing, customer.id);
      }
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : "";
    }
  }
};
</script>

<style lang="scss" scoped>
.link-suggestions {
  width: 100%;
  padding: 8px 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.intro {
  color: #4b5563;
  font-size: 0.9rem;
  margin: 0;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  .search {
    flex: 1 1 280px;
    min-width: 0;
  }
  .scope {
    flex: 0 0 auto;
  }
}
.index-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  font-size: 0.8rem;
  color: #6b7280;
}
.state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #6b7280;
  &.small {
    justify-content: flex-start;
    padding: 8px 0;
    font-size: 0.85rem;
  }
}
.rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 2fr;
  gap: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  &.is-duplicate {
    border-color: #fdba74;
  }
}
.duplicate-badge {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.8rem;
  font-weight: 600;
}
.duplicates-hint {
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.85rem;
}
.duplicates-link {
  color: #c2410c;
  font-weight: 600;
}
.web-user {
  min-width: 0;
  overflow-wrap: anywhere;
  .user-name {
    background: none;
    border: none;
    padding: 0;
    font-weight: 600;
    color: #1d4ed8;
    cursor: pointer;
    text-align: left;
    &:hover {
      text-decoration: underline;
    }
  }
  .activity {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #374151;
  }
}
.muted {
  color: #6b7280;
  font-size: 0.85rem;
}
.candidates {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.candidate {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
}
.candidate-info {
  min-width: 0;
  overflow-wrap: anywhere;
  .candidate-name {
    font-weight: 600;
    .muted {
      font-weight: 400;
      margin-left: 6px;
    }
  }
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.chip {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #374151;
  &.chip-email {
    background: #dcfce7;
    color: #166534;
  }
  &.chip-orders {
    background: #dbeafe;
    color: #1e40af;
  }
  &.chip-phone,
  &.chip-order_phone {
    background: #fef3c7;
    color: #92400e;
  }
}
.candidate-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.phone-search {
  display: flex;
  align-items: center;
  gap: 6px;
}
.link {
  background: none;
  border: none;
  color: #1d4ed8;
  cursor: pointer;
  padding: 0 4px;
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}
</style>
